import {
  Component,
  EventEmitter,
  Output,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { UntypedFormGroup, UntypedFormControl } from "@angular/forms";

@Component({
  selector: "app-load-simulation-filter",
  templateUrl: "./load-simulation-filter.component.html",
  styleUrls: ["./load-simulation-filter.component.css"],
})
// --- Component Description ---
// Component for filtering simulations by hierarchical entities (user, customer, region, project, simulation).
// Provides a dynamic filter form that updates available options and filtered lists
// based on the current selection at each level. Emits filter changes to parent components.
// Hierarchy: User → Customer → Region → Project → Simulation
export class LoadSimulationFilterComponent implements OnInit, OnChanges {
  // --- Private Properties ---
  // Internal flag to avoid auto-selecting user more than once.
  private hasAutoSelectedUser = false;
  private selectedUser: any;

  // --- Inputs ---
  // Input lists for each hierarchy level.
  @Input() users: any[] = [];
  @Input() customers: any[] = [];
  @Input() regions: any[] = [];
  @Input() projects: any[] = [];
  @Input() simulations: any[] = [];
  @Input() userId: number;
  @Input() userName: string;

  // --- Public Properties ---
  // Filtered lists for each hierarchy level, updated as the user selects values.
  filteredUsers: any[] = [];
  filteredCustomers: any[] = [];
  filteredRegions: any[] = [];
  filteredProjects: any[] = [];
  filteredSimulations: any[] = [];

  // --- Outputs ---
  // Emits the current filter form value whenever it changes.
  @Output() filterChange = new EventEmitter<any>();

  // --- Public Properties ---
  // Reactive form group for filter controls.
  filterForm: UntypedFormGroup;

  // --- Lifecycle Hooks ---
  // Initializes the filter form and subscriptions for value changes.
  ngOnInit() {
    this.filterForm = new UntypedFormGroup({
      user: new UntypedFormControl(null),
      customer: new UntypedFormControl(null),
      region: new UntypedFormControl(null),
      project: new UntypedFormControl(null),
      simulation: new UntypedFormControl(null),
    });

    this.filteredUsers = [...this.users];
    this.filteredCustomers = [...this.customers];
    this.filteredRegions = [...this.regions];
    this.filteredProjects = [...this.projects];
    this.filteredSimulations = [...this.simulations];

    this.filterForm.get("user").valueChanges.subscribe((user) => {
      this.filterByUser(user);
    });

    this.filterForm.get("customer").valueChanges.subscribe((customer) => {
      this.filterByCustomer(customer);
    });

    this.filterForm.get("region").valueChanges.subscribe((region) => {
      this.filterByRegion(region);
    });

    this.filterForm.get("project").valueChanges.subscribe((project) => {
      this.filterByProject(project);
    });

    this.filterForm.get("simulation").valueChanges.subscribe((simulation) => {
      this.filterBySimulation(simulation);
    });

    this.filterForm.valueChanges.subscribe((val) => {
      this.filterChange.emit(val);
    });
  }

  // --- Lifecycle Hooks ---
  // Handles changes to input lists, auto-selects user if needed.
  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.users &&
      this.users &&
      this.users.length > 0 &&
      !this.hasAutoSelectedUser
    ) {
      if (!this.selectedUser && !this.filterForm.get("user").value) {
        let usersCopy = [...this.users];
        if (this.userId && !usersCopy.some((u) => u.user_id === this.userId)) {
          usersCopy.push({
            id: this.userId,
            name: this.userName,
            selected: true,
            user_id: this.userId,
          });
        }
        this.users = usersCopy;
        this.filteredUsers = [...this.users];
      }
      this.selectedUser = this.users.find((u) => u.selected);
      this.filterForm.get("user").setValue(this.selectedUser);
      this.hasAutoSelectedUser = true;
    } else {
      this.filteredUsers = [...this.users];
      this.filteredCustomers = [...this.customers];
      this.filteredRegions = [...this.regions];
      this.filteredProjects = [...this.projects];
      this.filteredSimulations = [...this.simulations];
      this.filterForm?.updateValueAndValidity({ emitEvent: true });
    }
  }

  // --- Filtering Methods ---
  // Filters customers, regions, projects, and simulations by selected user.
  filterByUser(user: any) {
    if (!user) {
      this.resetFilters(["customer", "region", "project", "simulation"]);
      this.setAllFiltered();
      return;
    }
    this.filteredCustomers = this.customers.filter(
      (c) => c.parent_id === user.id
    );
    const customerIds = this.filteredCustomers.map((c) => c.id);
    this.filteredRegions = this.regions.filter((r) =>
      customerIds.includes(r.parent_id)
    );
    const regionIds = this.filteredRegions.map((r) => r.id);
    this.filteredProjects = this.projects.filter((p) =>
      regionIds.includes(p.parent_id)
    );
    const projectIds = this.filteredProjects.map((p) => p.id);
    this.filteredSimulations = this.simulations.filter((s) =>
      projectIds.includes(s.parent_id)
    );
    this.clearIfNotIncluded("customer", customerIds);
    this.clearIfNotIncluded("region", regionIds);
    this.clearIfNotIncluded("project", projectIds);
    this.clearIfNotIncluded(
      "simulation",
      this.filteredSimulations.map((s) => s.id)
    );
  }

  // Filters regions, projects, and simulations by selected customer.
  filterByCustomer(customer: any) {
    if (!customer) {
      this.resetFilters(["region", "project", "simulation"]);
      this.filterByUser(this.filterForm.get("user").value);
      return;
    }
    const user = this.users.find((u) => u.id === customer.parent_id);
    this.filteredUsers = user ? [user] : [];
    this.filteredRegions = this.regions.filter(
      (r) => r.parent_id === customer.id
    );
    const regionIds = this.filteredRegions.map((r) => r.id);
    this.filteredProjects = this.projects.filter((p) =>
      regionIds.includes(p.parent_id)
    );
    const projectIds = this.filteredProjects.map((p) => p.id);
    this.filteredSimulations = this.simulations.filter((s) =>
      projectIds.includes(s.parent_id)
    );
    this.setFilterValue("user", user);
    this.clearIfNotIncluded("region", regionIds);
    this.clearIfNotIncluded("project", projectIds);
    this.clearIfNotIncluded(
      "simulation",
      this.filteredSimulations.map((s) => s.id)
    );
  }

  // Filters projects and simulations by selected region.
  filterByRegion(region: any) {
    if (!region) {
      this.resetFilters(["project", "simulation"]);
      this.filterByCustomer(this.filterForm.get("customer").value);
      return;
    }
    const regionId = region.id;
    const customer = this.customers.find((c) => c.id === region.parent_id);
    this.filteredCustomers = customer ? [customer] : [];
    const user = customer
      ? this.users.find((u) => u.id === customer.parent_id)
      : null;
    this.filteredUsers = user ? [user] : [];
    this.filteredProjects = this.projects.filter(
      (p) => p.parent_id === regionId
    );
    const projectIds = this.filteredProjects.map((p) => p.id);
    this.filteredSimulations = this.simulations.filter((s) =>
      projectIds.includes(s.parent_id)
    );
    this.setFilterValue("customer", customer);
    this.setFilterValue("user", user);
    this.clearIfNotIncluded("project", projectIds);
    this.clearIfNotIncluded(
      "simulation",
      this.filteredSimulations.map((s) => s.id)
    );
  }

  // Filters simulations by selected project.
  filterByProject(project: any) {
    if (!project) {
      this.resetFilters(["simulation"]);
      this.filterByRegion(this.filterForm.get("region").value);
      return;
    }
    this.filteredSimulations = this.simulations.filter(
      (s) => s.parent_id === project.id
    );
    const region = this.regions.find((r) => r.id === project.parent_id);
    this.filteredRegions = region ? [region] : [];
    const customer = region
      ? this.customers.find((c) => c.id === region.parent_id)
      : null;
    this.filteredCustomers = customer ? [customer] : [];
    const user = customer
      ? this.users.find((u) => u.id === customer.parent_id)
      : null;
    this.filteredUsers = user ? [user] : [];
    this.setFilterValue("region", region);
    this.setFilterValue("customer", customer);
    this.setFilterValue("user", user);
    this.clearIfNotIncluded(
      "simulation",
      this.filteredSimulations.map((s) => s.id)
    );
  }

  // Updates all parent filters when a simulation is selected.
  filterBySimulation(simulation: any) {
    if (!simulation) {
      this.filterByProject(this.filterForm.get("project").value);
      return;
    }
    const project = this.projects.find((p) => p.id === simulation.parent_id);
    this.filteredProjects = project ? [project] : [];
    const region = project
      ? this.regions.find((r) => r.id === project.parent_id)
      : null;
    this.filteredRegions = region ? [region] : [];
    const customer = region
      ? this.customers.find((c) => c.id === region.parent_id)
      : null;
    this.filteredCustomers = customer ? [customer] : [];
    const user = customer
      ? this.users.find((u) => u.id === customer.parent_id)
      : null;
    this.filteredUsers = user ? [user] : [];
    this.setFilterValue("project", project);
    this.setFilterValue("region", region);
    this.setFilterValue("customer", customer);
    this.setFilterValue("user", user);
  }

  // --- Public Methods ---
  // Resets the given filter fields to null without emitting events.
  resetFilters(fields: string[], isResetBtnEvent: boolean = false) {
    if (isResetBtnEvent) {
      fields.forEach((f) => {
        if (f === "user" && isResetBtnEvent && this.selectedUser) {
          this.filterForm
            .get("user")
            .setValue(this.selectedUser, { emitEvent: false });
        } else {
          this.filterForm.get(f).setValue(null, { emitEvent: false });
        }
      });

      this.filterChange.emit(this.filterForm.value);
    } else {
      fields.forEach((f) =>
        this.filterForm.get(f).setValue(null, { emitEvent: false })
      );
    }
  }

  // Sets all filtered lists to the full input lists.
  setAllFiltered() {
    this.filteredUsers = [...this.users];
    this.filteredCustomers = [...this.customers];
    this.filteredRegions = [...this.regions];
    this.filteredProjects = [...this.projects];
    this.filteredSimulations = [...this.simulations];
  }

  // Sets a filter form control value without emitting events.
  private setFilterValue(field: string, value: any) {
    this.filterForm
      .get(field)
      .setValue(value ? value : null, { emitEvent: false });
  }

  // Clears a filter field if its value is not in the validIds list.
  private clearIfNotIncluded(field: string, validIds: any[]) {
    const val = this.filterForm.get(field).value;
    if (val && !validIds.includes(val.id)) {
      this.filterForm.get(field).setValue(null);
    }
  }
}
